import React from 'react';
import { realName } from '@/lib/contactName';
import { MessageCircle, Instagram, Facebook, X, Mic, Camera, Video, Play } from 'lucide-react';
import { api } from '@/lib/api';

const CHANNEL = {
  WhatsApp: ['bg-[#25D366]', MessageCircle],
  Messenger: ['bg-[#0866FF]', Facebook],
  Instagram: ['bg-gradient-to-tr from-[#F58529] via-[#DD2A7B] to-[#8134AF]', Instagram],
};
const initials = (n) => (n || '?').replace(/[^\p{L}\p{N} ]/gu, '').split(' ').filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase() || '?';
const KIND = { image: 'photo', photo: 'photo', sticker: 'photo', video: 'video', audio: 'voice', voice: 'voice' };
export const mediaKind = (ev) => KIND[(ev.media?.type || /^\((\w+)\)$/.exec((ev.preview || '').trim())?.[1] || '').toLowerCase()];

const Avatar = ({ url, name, channel }) => {
  const [broken, setBroken] = React.useState(false);
  const [tone, Icon] = CHANNEL[channel] || ['bg-slate-500', MessageCircle];
  return (
    <div className="relative h-9 w-9 shrink-0">
      {url && !broken
        ? <img src={url} alt="" onError={() => setBroken(true)} className="h-9 w-9 rounded-full object-cover" />
        : <div className="h-9 w-9 rounded-full bg-gradient-to-b from-slate-300 to-slate-400 grid place-items-center text-[13px] font-semibold text-white">{initials(name)}</div>}
      <span className={`absolute -bottom-0.5 -right-0.5 h-4 w-4 rounded-full ring-2 ring-white/90 grid place-items-center text-white ${tone}`} data-testid="message-banner-channel">
        <Icon className="h-2.5 w-2.5" strokeWidth={2.5} />
      </span>
    </div>
  );
};

// Photo / video still of the incoming message; it may still be downloading, so one retry.
const MediaThumb = ({ media, kind }) => {
  const [url, setUrl] = React.useState(null);
  React.useEffect(() => {
    let live = true; let u = null; let t;
    const get = (left) => api.get(`/media/${media.id}/preview`, { responseType: 'blob' })
      .then(({ data }) => { u = URL.createObjectURL(data); if (live) setUrl(u); })
      .catch(() => { if (left && live) t = setTimeout(() => get(left - 1), 2500); });
    get(1);
    return () => { live = false; clearTimeout(t); if (u) URL.revokeObjectURL(u); };
  }, [media.id]);
  if (!url) return null;
  return (
    <span className="relative h-11 w-11 shrink-0 overflow-hidden rounded-lg ring-1 ring-black/5" data-testid="message-banner-thumb">
      <img src={url} alt="" className="h-full w-full object-cover animate-in fade-in duration-200" />
      {kind === 'video' && <span className="absolute inset-0 grid place-items-center bg-black/20"><Play className="h-3.5 w-3.5 text-white fill-current" /></span>}
    </span>
  );
};

const LABEL_ICON = { photo: Camera, video: Video, voice: Mic };

// Apple-style notification: frosted card, app line, sender + message. Tap opens; X or swipe closes.
export function MessageBanner({ ev, title, text, count = 1, sticky, onOpen, onClose }) {
  const kind = mediaKind(ev);
  const LabelIcon = LABEL_ICON[kind];
  const down = React.useRef(null);
  const tap = (e) => { const d = down.current; if (d && Math.hypot(e.clientX - d.x, e.clientY - d.y) > 8) return; onOpen(); };
  return (
    <div role="button" tabIndex={0} onPointerDown={(e) => { down.current = { x: e.clientX, y: e.clientY }; }} onClick={tap} onKeyDown={(e) => e.key === 'Enter' && onOpen()}
      data-testid="message-banner" data-count={count} data-sticky={sticky ? 'true' : 'false'}
      className="group relative w-[var(--width,356px)] max-[600px]:w-full cursor-pointer select-none rounded-[14px] border border-black/[0.06] bg-white/80 px-3.5 pt-2.5 pb-3 text-left shadow-[0_10px_30px_rgba(0,0,0,0.12),0_1px_3px_rgba(0,0,0,0.08)] backdrop-blur-2xl backdrop-saturate-150 transition-transform duration-150 active:scale-[0.985]">
      <button type="button" onClick={(e) => { e.stopPropagation(); onClose(); }} aria-label="Close"
        className="absolute -left-2 -top-2 h-6 w-6 grid place-items-center rounded-full border border-black/[0.06] bg-white/95 text-slate-500 shadow-sm transition-opacity duration-150 hover:text-[#1D1D1F] [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover:opacity-100 focus:opacity-100"
        data-testid="message-banner-close"><X className="h-3 w-3" strokeWidth={2.5} /></button>
      <div className="mb-2 flex items-center gap-1.5 text-[11px] text-slate-500">
        <img src="/verse-mark-v.png" alt="" className="h-4 w-4 rounded-[4px] bg-white" />
        <span className="font-medium uppercase tracking-[0.06em]">Verse Clinic</span>
        {ev.channel && <span className="truncate">· {ev.channel}</span>}
        <span className="ml-auto shrink-0">{count > 1 ? `${count} new` : 'now'}</span>
      </div>
      <div className="flex items-start gap-3">
        <Avatar url={ev.avatar} name={realName(ev) || ''} channel={ev.channel} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="truncate text-[14px] font-semibold leading-tight text-[#1D1D1F]" data-testid="message-banner-name">{title}</span>
            {count > 1 && <span className="shrink-0 rounded-full bg-[#1D1D1F]/[0.06] px-1.5 text-[10px] font-medium leading-4 text-slate-600 tabular-nums" data-testid="message-banner-count">{count} messages</span>}
          </div>
          <p className="mt-0.5 line-clamp-2 text-[13px] leading-snug text-slate-600" data-testid="message-banner-text">
            {LabelIcon && <LabelIcon className="mr-1 -mt-0.5 inline h-3.5 w-3.5 text-slate-400" data-testid={`message-banner-${kind}`} />}{text}
          </p>
        </div>
        {ev.media && kind !== 'voice' && <MediaThumb key={ev.media.id} media={ev.media} kind={kind} />}
      </div>
    </div>
  );
}

// Several people at once: one folded card instead of a pile of pop-ups.
export function StackBanner({ people, count, leads, onOpen, onClose }) {
  const names = people.slice(0, 3).join(', ') + (people.length > 3 ? ` +${people.length - 3}` : '');
  return (
    <div role="button" tabIndex={0} onClick={onOpen} onKeyDown={(e) => e.key === 'Enter' && onOpen()} data-testid="message-stack-banner" data-people={people.length}
      className="group relative w-[var(--width,356px)] max-[600px]:w-full cursor-pointer select-none rounded-[14px] border border-black/[0.06] bg-white/80 px-3.5 pt-2.5 pb-3 text-left shadow-[0_10px_30px_rgba(0,0,0,0.12),0_1px_3px_rgba(0,0,0,0.08)] backdrop-blur-2xl backdrop-saturate-150 transition-transform duration-150 active:scale-[0.985]">
      <button type="button" onClick={(e) => { e.stopPropagation(); onClose(); }} aria-label="Close"
        className="absolute -left-2 -top-2 h-6 w-6 grid place-items-center rounded-full border border-black/[0.06] bg-white/95 text-slate-500 shadow-sm hover:text-[#1D1D1F] [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover:opacity-100 focus:opacity-100"
        data-testid="message-stack-close"><X className="h-3 w-3" strokeWidth={2.5} /></button>
      <div className="mb-1.5 flex items-center gap-1.5 text-[11px] text-slate-500">
        <img src="/verse-mark-v.png" alt="" className="h-4 w-4 rounded-[4px] bg-white" />
        <span className="font-medium uppercase tracking-[0.06em]">Verse Clinic</span>
        <span className="ml-auto shrink-0">now</span>
      </div>
      <div className="text-[14px] font-semibold leading-tight text-[#1D1D1F]" data-testid="message-stack-title">
        {people.length} new chats · {count} messages{leads > 0 && <span className="ml-1.5 rounded-full bg-[#C7A46A]/20 px-1.5 text-[11px] font-medium text-[#8A6A33]">{leads} new lead{leads > 1 ? 's' : ''}</span>}
      </div>
      <p className="mt-0.5 truncate text-[13px] text-slate-600" data-testid="message-stack-names">{names}</p>
      <div className="mt-2 text-[12px] font-medium text-[#0A84FF]" data-testid="message-stack-open">Open inbox</div>
    </div>
  );
}
